import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {chain, findIndex} from 'lodash';
import {BehaviorSubject} from 'rxjs';

import {CustomViewService} from '../../../../services/custom-view.service';
import {SelectableItems} from '../../dialogs/models/create-new-view.enum';
import {
  IColumn,
  IColumnField,
  ISelectedColumnsEmit
} from '../../dialogs/models/create-new-view.interface';

@Component({
  selector: 'app-select-columns-view',
  templateUrl: './select-columns-view.component.html',
  styleUrls: ['./select-columns-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectColumnsViewComponent implements OnInit, OnDestroy {
  @Input() public confirmBtn = 'RUBIC_CUBE.NEXT_BTN';
  @Input() public cancelBtn = 'POPUPS.SWITCH_BACK';
  @Output() public confirmColumnsSelect: EventEmitter<ISelectedColumnsEmit> =
    new EventEmitter<ISelectedColumnsEmit>();
  @Output() public cancelColumnsSelect: EventEmitter<void> = new EventEmitter<void>();
  /** Live validity of this step, emitted on every change so the host can gate navigation. */
  @Output() public columnsStateChange: EventEmitter<{valid: boolean}> = new EventEmitter<{
    valid: boolean;
  }>();

  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public selectableType = SelectableItems.COLUMN;
  public isValid: boolean;
  public availableColumns: IColumnField[] = [];
  public selectedColumns: IColumnField[] = [];
  private columns: IColumnField[] = [];
  private preSavedValues: ISelectedColumnsEmit;

  constructor(private customViewService: CustomViewService) {}

  ngOnInit(): void {
    this.preSavedValues = this.customViewService.retrieveSelectedColumns();

    this._initColumns();
    this.validate();

    this.loading$.next(false);
  }

  public confirm(): void {
    this.confirmColumnsSelect.emit({selectedColumns: this.buildSelectedColumns()});
  }

  public cancel(): void {
    this.cancelColumnsSelect.emit();
  }

  ngOnDestroy(): void {
    this.persistState();
  }

  private buildSelectedColumns(): IColumn[] {
    const filterOnlyColumns = this.customViewService.retrieveFilterOnlyColumns();

    return [...this.selectedColumns, ...filterOnlyColumns]
      .filter(Boolean)
      .map(field => ({field, operator: '', editTemplate: field.valueType}) as IColumn);
  }

  /**
   * Mirror the current selection into the shared store on every change, so leaving
   * the step via the stepper header never loses edits.
   */
  private persistState(): void {
    this.customViewService.storeSelectedColumns({selectedColumns: this.buildSelectedColumns()});
    this.columnsStateChange.emit({valid: this.isValid});
  }

  private _initColumns(): void {
    this.retrieveColumns();

    const selected = this.preSavedValues.selectedColumns?.filter(c => !c.field.filterOnly);

    const filterOnlyColumns = chain(this.columns)
      .filter(c => c.filterOnly)
      .map(c => ({...c, hide: true}))
      .value();
    this.columns = this.columns.filter(c => !c.filterOnly);

    for (const column of this.columns) {
      if (column.value === 'name') {
        column.locked = true;
        this.selectedColumns.push(column);
      } else {
        const selectedColumnIndex = findIndex(selected, {field: {value: column.value}});

        if (selectedColumnIndex !== -1) {
          this.selectedColumns[selectedColumnIndex] = column;
        } else {
          this.availableColumns.push(column);
        }
      }
    }
    this.customViewService.setFilterOnlyColumns(filterOnlyColumns);
  }

  public validate(): void {
    this.isValid = this.selectedColumns.filter(c => !c.filterOnly).length >= 2;

    this.persistState();
  }

  private retrieveColumns(): void {
    const availableColumns = this.customViewService.retrieveRefModelColumns();
    this.columns = this.customViewService.flatSubcolumns(availableColumns);
  }
}
